/**
 * PaymentRowEditor — the shared "edit a payment row" body. Single source
 * of truth for the field grid (amount, currency, payment method,
 * description, status, date, notes) used by:
 *
 *   - `DraftPaymentsSection` (Add/Edit Segment dialogs) wraps this in a
 *     blue-bordered card with a remove-row button + "New payment #N"
 *     header.
 *   - `InlinePaymentForm` (ItineraryView quick-add) wraps this with a
 *     Save/Cancel button row inside a Card.
 *
 * Keeping the field grid in one place is what unifies the look across
 * both contexts — the user explicitly asked for that parity.
 *
 * Props:
 *   payment        — { amount, currency, payment_method_id, description,
 *                      status, payment_date, notes } object
 *   onChange       — function(patch) — called with a partial patch the
 *                    caller must merge into its own state. Keeps this
 *                    component fully controlled / state-bag-agnostic.
 *   paymentMethods — array from /api/payment-methods. Sorted internally.
 *   segmentCountry — biases the currency picker to the local currency.
 *   disabled       — read-only mode (used while parent is mid-save).
 *   testIdPrefix   — prefix for data-testids on each field. Different
 *                    prefixes let parent contexts (drafts vs inline)
 *                    target the right row in tests.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { sortPaymentMethods } from "@/utils/paymentMethodSort";
import CurrencySelectFiltered from "@/components/shared/CurrencySelectFiltered";
import { getDescriptionOptions, getDescriptionLabel } from "@/utils/paymentDescriptions";

const PaymentRowEditor = ({
  payment,
  onChange,
  paymentMethods = [],
  segmentCountry,
  segmentType,
  disabled = false,
  testIdPrefix = "payment-row",
}) => {
  const sorted = sortPaymentMethods(paymentMethods);

  // Per-segment-type description options. When the parent didn't pass a
  // segmentType (e.g. the orphan/standalone Edit Payment dialog) we get
  // the union list. Saved values that aren't in the active list still
  // render correctly because we inject a "ghost" option for them below.
  const descriptionOptions = getDescriptionOptions(segmentType);
  const currentValue = payment.description || "fare";
  const hasCurrentInList = descriptionOptions.some((o) => o.value === currentValue);

  // Selecting a points-program method auto-flips currency to POINTS so the
  // amount field renders the integer step + placeholder; selecting a normal
  // card flips back to a real currency (preserve existing if non-points,
  // otherwise default USD).
  const handleMethodChange = (val) => {
    const method = paymentMethods.find((m) => m.id === val);
    const isPoints = method?.payment_type === "points";
    onChange({
      payment_method_id: val || "",
      currency: isPoints
        ? "POINTS"
        : (payment.currency && payment.currency !== "POINTS" ? payment.currency : "USD"),
    });
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">{payment.currency === "POINTS" ? "Points" : "Amount"} *</Label>
          <Input
            type="number"
            step={payment.currency === "POINTS" ? "1" : "0.01"}
            placeholder={payment.currency === "POINTS" ? "10000" : "0.00"}
            value={payment.amount}
            onChange={(e) => onChange({ amount: e.target.value })}
            className="h-9"
            disabled={disabled}
            data-testid={`${testIdPrefix}-amount`}
          />
        </div>
        <div>
          <Label className="text-xs">Currency</Label>
          <CurrencySelectFiltered
            value={payment.currency || "USD"}
            onChange={(val) => onChange({ currency: val })}
            segmentCountry={segmentCountry}
            testId={`${testIdPrefix}-currency`}
            className="h-9 text-sm"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs">Payment Method</Label>
        <Select
          value={payment.payment_method_id || ""}
          onValueChange={handleMethodChange}
          disabled={disabled}
        >
          <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-method`}>
            <SelectValue placeholder="Select payment method (optional)" />
          </SelectTrigger>
          <SelectContent position="popper" className="z-[100]">
            {sorted.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.payment_type === "points" && "⭐ "}{m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Description</Label>
          <Select
            value={payment.description || "fare"}
            onValueChange={(val) => onChange({ description: val })}
            disabled={disabled}
          >
            <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-description`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper">
              {!hasCurrentInList && currentValue && (
                /* Legacy / cross-type saved value — surface it as a
                   ghost option so the select shows a label instead of
                   blank. User-confirmed UX: don't auto-migrate, just
                   render. */
                <SelectItem value={currentValue}>
                  {getDescriptionLabel(currentValue)}
                </SelectItem>
              )}
              {descriptionOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select
            value={payment.status || "paid"}
            onValueChange={(val) => onChange({ status: val })}
            disabled={disabled}
          >
            <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-status`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="planned">Planned</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Date</Label>
          <Input
            type="date"
            value={payment.payment_date || ""}
            onChange={(e) => onChange({ payment_date: e.target.value })}
            disabled={disabled}
            className="h-9"
            data-testid={`${testIdPrefix}-date`}
          />
        </div>
      </div>

      <div>
        <Label className="text-xs">Notes</Label>
        <Textarea
          placeholder="Additional information..."
          value={payment.notes || ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          disabled={disabled}
          rows={2}
          className="text-sm"
          data-testid={`${testIdPrefix}-notes`}
        />
      </div>
    </div>
  );
};

export default PaymentRowEditor;
